import React from 'react';

import { FormFieldType } from '@components/dynamic-form/types/inputTypes';
import { useResetKey } from '@hooks/useResetKey';
import { MAIN_THEME } from '@styles';

import RepeaterBoxInputField from './RepeaterBoxInputField';
import { IRepeaterBoxFormFieldProps } from '.';

import clsx from 'clsx';
import { cloneDeep } from 'lodash';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(() => ({
  container: {
    borderRadius: '4px',
    padding: '0 6.5px 10px 6.5px',
    position: 'relative',
    margin: '0 -6.5px'
  },
  multiContainer: {
    paddingTop: '20px',
    backgroundColor: MAIN_THEME.color.white98
  },
  title: {
    color: MAIN_THEME.color.black,
    fontSize: MAIN_THEME.fontSize.sm,
    fontFamily: MAIN_THEME.font.bold,
    marginBottom: '10px'
  },
  closedButton: {
    position: 'absolute',
    right: '10px',
    top: '5px',
    border: 0,
    backgroundColor: 'transparent',
    padding: 0,
    color: MAIN_THEME.color.white65,
    fontSize: MAIN_THEME.fontSize.rg,
    fontFamily: MAIN_THEME.font.regular,
    cursor: 'pointer',
    '&:hover:not(:disabled)': {
      color: MAIN_THEME.color.red
    }
  },
  rowContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  addButton: {
    border: 0,
    backgroundColor: 'transparent',
    padding: 0,
    color: 'rgba(211, 13, 43, 0.8)',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: MAIN_THEME.font.regular,
    fontSize: MAIN_THEME.fontSize.sm,
    textDecoration: 'underline',
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed'
    }
  }
}));

const RepeaterBoxFormField: React.FC<IRepeaterBoxFormFieldProps> = ({
  title,
  values,
  inputs,
  filterDuplicatesByKey,
  addButtonText,
  dynamicAddButtonText,
  minRepeaterLength,
  maxRepeaterLength,
  loadingFields,
  onChange,
  onBlur,
  onAutocompleteSearch,
  error = [],
  touched = [],
  onAddBox,
  onDeleteBox,
  disabled
}) => {
  const { classes } = useStyles();
  const [autocompleteKey, rerenderAutocomplete] =
    useResetKey('autocompleteKey');

  const canAdd = values.length < maxRepeaterLength!;
  const canDelete = values.length > minRepeaterLength!;

  const getRowInputs = (rowIndex: number) => {
    const shouldFilter = !!filterDuplicatesByKey;

    const excludeIds = shouldFilter
      ? values
          .map((v) => v[filterDuplicatesByKey])
          .filter(
            (id) => !!id && id !== values[rowIndex][filterDuplicatesByKey]
          )
      : [];

    return Object.keys(inputs).reduce(
      (acc, key) => {
        const def = inputs[key];

        if (
          shouldFilter &&
          def.type === FormFieldType.select &&
          'selectOptions' in def
        ) {
          const filteredOptions = Object.fromEntries(
            Object.entries(def.selectOptions!).filter(
              ([id]) =>
                id === values[rowIndex][filterDuplicatesByKey!] ||
                !excludeIds.includes(id)
            )
          );

          acc[key] = { ...(def as any), selectOptions: filteredOptions };
        } else {
          acc[key] = def;
        }

        return acc;
      },
      {} as typeof inputs
    );
  };

  const addNewRecord = () => {
    if (!canAdd) return;
    const newValues = cloneDeep(values);
    newValues.push(
      Object.fromEntries(Object.keys(inputs).map((k) => [k, ''])) as any
    );
    onChange(newValues);
    onAddBox?.();
  };

  const removeRecord = (index: number) => {
    const newValues = cloneDeep(values);
    newValues.splice(index, 1);
    onDeleteBox?.(index);
    onChange(newValues);
    rerenderAutocomplete();
  };

  const onFieldChange = (name: string, val: string, row: number) => {
    const newValues = cloneDeep(values);
    newValues[row][name] = val;
    onChange(newValues, name);
  };

  const onFieldBlur = (name: string, row: number) => {
    onBlur?.(name, row);
  };

  return (
    <div>
      {title && <div className={classes.title}>{title}</div>}
      <div className={classes.rowContainer}>
        {values.map((row, i) => {
          const rowInputs = getRowInputs(i);
          const rowErrors = error[i] || {};
          const rowTouched = touched[i] || {};

          return (
            <React.Fragment key={i}>
              <div
                className={clsx(classes.container, {
                  [classes.multiContainer]: values.length > 1
                })}
              >
                {canDelete && !disabled && (
                  <button
                    type="button"
                    className={classes.closedButton}
                    onClick={() => removeRecord(i)}
                  >
                    ✖︎
                  </button>
                )}
                {Object.values(rowInputs).map((inputDef) => (
                  <RepeaterBoxInputField
                    key={inputDef.name}
                    autocompleteKey={autocompleteKey}
                    value={row[inputDef.name]}
                    rowIndex={i}
                    input={inputDef}
                    error={rowErrors[inputDef.name]}
                    touched={rowTouched[inputDef.name]}
                    loading={loadingFields?.[inputDef.name]}
                    disabled={disabled}
                    onFieldChange={onFieldChange}
                    onFieldBlur={onFieldBlur}
                    onAutocompleteSearch={onAutocompleteSearch}
                  />
                ))}
              </div>
              {canAdd && i === values.length - 1 && (
                <button
                  type="button"
                  className={classes.addButton}
                  onClick={addNewRecord}
                  disabled={disabled}
                >
                  {dynamicAddButtonText?.[i] || addButtonText}
                </button>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default RepeaterBoxFormField;
